import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input } from '@angular/core';
import {PersonLogin, RoleName} from '../../person/person-login';
import {PersonLoginService} from '../../services/person-login-service';
import {ProductService} from '../../services/product-service';
import {Router} from '@angular/router';
import {SpecificationCharacteristicService} from '../../services/specification-characteristic.service';
import {StringUtils} from '../../utils/string-utils';
import {Product} from '../product';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Observable} from 'rxjs';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {startWith, map} from 'rxjs/operators';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'app-product-sku-slots',
    templateUrl: './product-sku-slots.component.html',
    styleUrls: ['./product-sku-slots.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, MatAutocomplete, MatOption, MatInput, FormsModule, MatAutocompleteTrigger, ReactiveFormsModule, MatProgressSpinner, AsyncPipe]
})
export class ProductSkuSlotsComponent implements OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly productService = inject(ProductService);
  private readonly router = inject(Router);
  private readonly specificationCharacteristicService = inject(SpecificationCharacteristicService);
  private readonly snackBar = inject(MatSnackBar);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly productId = input.required<string>();
  personLogin!: PersonLogin;
  readonly product = signal<Product>(undefined!);
  readonly availableSlots = signal<string[]>(undefined!);
  readonly existingSlots = signal<string[]>(undefined!);
  readonly newSlotName = signal<string>(undefined!);
  readonly selectedExistingSlot = signal<string>(undefined!);
  filteredAvailable!: Observable<string[]>;
  availableSkuInput = new UntypedFormControl('');

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  ngOnInit(): void {
    if (!this.personLoginService.hasRole(RoleName.IECIT)) {
      this.router.navigate(['main']);
    }
    this.personLogin = this.personLoginService.getCachedLogin()!;
    if (!this.productId() || this.productId().trim().length === 0) {
      this.backToProductSearch();
    }
    this.isBusy.set(true);
    this.productService.getProductSkuSlots(this.productId()).subscribe({
      next: (slots) => this.existingSlots.set(slots),
      error: (error) => this.handleError(error)
    });
    this.productService.loadProduct(this.productId()).subscribe({
      next: (product) => this.product.set(product),
      error: (error) => this.handleError(error),
      complete: () => this.isBusy.set(false)
    });
    this.availableSlots.set([]);
    this.specificationCharacteristicService.getAllSkuSlots().subscribe({
      next: (slots) => this.availableSlots.set(slots),
      error: (error) => this.handleError(error)
    });
    this.filteredAvailable = this.availableSkuInput.valueChanges.pipe(
      startWith(''),
      map(value => this.filterAvailable(value || '')),
    );
  }

  private filterAvailable(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.availableSlots().filter(slot => slot.toLowerCase().includes(filterValue));
  }

  private backToProductSearch(): void {
    this.router.navigate(['product']);
  }

  backToProductDetail(): void {
    this.router.navigate(['product', this.productId()]);
  }

  saveProductSkuSlots(): void {
    this.isBusy.set(true);
    this.productService.saveProductSkuSlots(this.productId(), this.existingSlots()).subscribe({
      error: (error) => this.handleError(error),
      complete: () => {
        this.selectedExistingSlot.set(undefined!);
        this.newSlotName.set(undefined!);
        this.isBusy.set(false);
        this.snackBar.open('Successfully updated', 'Close');
      }
    });
  }

  addSlot(): void {
    if (!this.existingSlots()) {
      this.existingSlots.set([]);
    }
    if (this.newSlotName()?.trim()?.length > 0) {
      if (this.existingSlots().filter(slot => slot === this.newSlotName()).length === 0) {
        this.existingSlots.update(current => [...current, this.newSlotName()]);
      }
    }
    this.newSlotName.set(undefined!);
  }

  removeExistingSlot(): void {
    if (this.selectedExistingSlot() && this.selectedExistingSlot().trim().length > 0) {
      const index = this.existingSlots().indexOf(this.selectedExistingSlot());
      if (index >= 0) {
        this.existingSlots.update(current => current.filter((_, slotIndex) => slotIndex !== index));
        this.selectedExistingSlot.set(undefined!);
      }
    }
  }

}
